import React from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { Search, UserRound, Phone, Mail, ArrowRight, Plus, X, Shield, Building2 } from "lucide-react";
import CityCombobox from "../../components/CityCombobox";

const emptyClient = { name: "", phone: "", email: "", address: "", county: "", city: "", company: "", cui: "", notes: "" };

export default function AdminClients() {
    const [items, setItems] = React.useState([]);
    const [q, setQ] = React.useState("");
    const [filter, setFilter] = React.useState("all"); // all | registered | offline
    const [open, setOpen] = React.useState(false);
    const [draft, setDraft] = React.useState(emptyClient);
    const [saving, setSaving] = React.useState(false);
    const [counties, setCounties] = React.useState([]);
    const [cities, setCities] = React.useState([]);

    React.useEffect(() => {
        api.get("/locations/counties").then(r => setCounties(r.data || [])).catch(() => {});
    }, []);
    React.useEffect(() => {
        if (draft.county) api.get("/locations/cities", { params: { county: draft.county } }).then(r => setCities(r.data || [])).catch(() => {});
        else setCities([]);
    }, [draft.county]);

    const load = React.useCallback(() => {
        api.get(`/admin/clients/unified?q=${encodeURIComponent(q)}`)
            .then(r => setItems(r.data?.items || []))
            .catch(() => {});
    }, [q]);

    React.useEffect(() => {
        const t = setTimeout(load, 250);
        return () => clearTimeout(t);
    }, [load]);

    const filtered = items.filter(c => filter === "all" ? true : c.type === filter);
    const counts = {
        all: items.length,
        registered: items.filter(c => c.type === "registered").length,
        offline: items.filter(c => c.type === "offline").length,
    };

    const saveNew = async () => {
        if (!draft.name.trim()) return toast.error("Numele este obligatoriu");
        setSaving(true);
        try {
            await api.post("/admin/clients/offline", draft);
            toast.success("Client adăugat");
            setDraft(emptyClient); setOpen(false); load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Eroare");
        }
        setSaving(false);
    };

    const dossierLink = (c) => {
        if (c.type === "offline") {
            return `/admin/clients/dossier?client_id=${c.key_client_id}`;
        }
        const p = new URLSearchParams();
        if (c.key_user_id) p.set("user_id", c.key_user_id);
        if (c.phone) p.set("phone", c.phone);
        if (c.email) p.set("email", c.email);
        return `/admin/clients/dossier?${p.toString()}`;
    };

    return (
        <div data-testid="admin-clients">
            <div className="flex items-center justify-between mb-4">
                <div><div className="overline mb-1">Administrare</div><h1 className="font-heading text-3xl">Clienți</h1></div>
                <button onClick={() => setOpen(true)} className="btn-primary flex items-center gap-2" data-testid="clients-create-offline">
                    <Plus className="h-4 w-4" /> Client offline
                </button>
            </div>

            <div className="card-soft p-3 mb-4 flex items-center gap-2">
                <Search className="h-4 w-4 text-muted-foreground" />
                <input value={q} onChange={(e) => setQ(e.target.value)}
                       placeholder="Caută după nume, telefon, email, companie..."
                       className="input-soft !py-2 flex-1" data-testid="clients-search" />
            </div>

            <div className="flex gap-1 mb-4 border-b border-border">
                {[["all", "Toți"], ["registered", "Cu cont"], ["offline", "Offline"]].map(([k, l]) => (
                    <button key={k} onClick={() => setFilter(k)}
                            className={`px-4 py-2 text-sm font-medium ${filter === k ? "border-b-2 border-primary text-primary" : "text-muted-foreground"}`}
                            data-testid={`clients-filter-${k}`}>
                        {l} ({counts[k]})
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {filtered.length === 0 && (
                    <p className="col-span-full text-center text-muted-foreground py-8">Niciun client.</p>
                )}
                {filtered.map(c => (
                    <Link key={(c.key_user_id || c.key_client_id) + c.type} to={dossierLink(c)}
                          className="card-soft p-4 hover:border-primary flex items-center gap-3"
                          data-testid={`client-${c.type}-${c.key_user_id || c.key_client_id}`}>
                        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                            {c.type === "registered" ? <UserRound className="h-5 w-5 text-primary" /> : <Building2 className="h-5 w-5 text-amber-600" />}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-medium truncate flex items-center gap-2">
                                {c.name || "—"}
                                {c.type === "registered" && (c.roles || []).some(r => ["admin", "superadmin", "installer", "sales"].includes(r)) && (
                                    <span className="chip-primary text-[10px] flex items-center gap-1"><Shield className="h-2.5 w-2.5" />Staff</span>
                                )}
                                {c.type === "registered" ? (
                                    <span className="chip text-[10px] bg-primary/10 text-primary">cont</span>
                                ) : (
                                    <span className="chip text-[10px] bg-amber-100 text-amber-900">offline</span>
                                )}
                                {c.company && <span className="text-xs text-muted-foreground">· {c.company}</span>}
                            </div>
                            <div className="text-xs text-muted-foreground flex gap-3 truncate">
                                {c.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{c.phone}</span>}
                                {c.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{c.email}</span>}
                            </div>
                        </div>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    </Link>
                ))}
            </div>

            {open && (
                <div className="fixed inset-0 z-50 bg-foreground/50 flex items-center justify-center p-4">
                    <div className="bg-card rounded-2xl max-w-md w-full p-6 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="client-offline-modal">
                        <div className="flex items-center justify-between">
                            <h2 className="font-heading text-xl">Client offline nou</h2>
                            <button onClick={() => setOpen(false)}><X className="h-5 w-5" /></button>
                        </div>
                        <p className="text-xs text-muted-foreground">Persoane/firme care nu au cont pe site dar lucrezi cu ei (B2B, pe teren, recomandări).</p>
                        <input required placeholder="Nume *" value={draft.name}
                               onChange={(e) => setDraft(d => ({ ...d, name: e.target.value }))}
                               className="input-soft" data-testid="oc-name" />
                        <div className="grid grid-cols-2 gap-2">
                            <input placeholder="Telefon" value={draft.phone}
                                   onChange={(e) => setDraft(d => ({ ...d, phone: e.target.value }))}
                                   className="input-soft" data-testid="oc-phone" />
                            <input type="email" placeholder="Email" value={draft.email}
                                   onChange={(e) => setDraft(d => ({ ...d, email: e.target.value }))}
                                   className="input-soft" data-testid="oc-email" />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <input placeholder="Companie (opțional)" value={draft.company}
                                   onChange={(e) => setDraft(d => ({ ...d, company: e.target.value }))}
                                   className="input-soft" data-testid="oc-company" />
                            <input placeholder="CUI (opțional)" value={draft.cui}
                                   onChange={(e) => setDraft(d => ({ ...d, cui: e.target.value }))}
                                   className="input-soft" />
                        </div>
                        <input placeholder="Adresă" value={draft.address}
                               onChange={(e) => setDraft(d => ({ ...d, address: e.target.value }))}
                               className="input-soft" />
                        <div className="grid grid-cols-2 gap-2">
                            <select value={draft.county} onChange={(e) => setDraft(d => ({ ...d, county: e.target.value, city: "" }))} className="input-soft">
                                <option value="">Județ</option>
                                {counties.map(c => <option key={c}>{c}</option>)}
                            </select>
                            <CityCombobox
                                cities={cities}
                                value={draft.city}
                                onChange={(v) => setDraft(d => ({ ...d, city: v }))}
                                placeholder="Localitate"
                            />
                        </div>
                        <textarea rows="3" placeholder="Note interne" value={draft.notes}
                                  onChange={(e) => setDraft(d => ({ ...d, notes: e.target.value }))}
                                  className="input-soft" />
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setOpen(false)} className="btn-outline">Anulează</button>
                            <button onClick={saveNew} disabled={saving} className="btn-primary" data-testid="oc-save">
                                {saving ? "Se salvează..." : "Salvează client"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
